import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getFacilityDetails, PricingInfo } from '../services/api';
import PricingDisplay from './PricingDisplay';

interface FacilityDetailsProps {
  facilityId: string;
  facilityName: string;
  onClose: () => void;
  pricingInfo?: PricingInfo;
}

const FacilityDetails: React.FC<FacilityDetailsProps> = ({ facilityId, facilityName, onClose, pricingInfo }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFacilityDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = await getAccessTokenSilently();
        const response = await getFacilityDetails(token, facilityId);
        
        setDetails(response.details);
      } catch (err) {
        console.error('Failed to load facility details:', err);
        setError('Failed to load facility details');
      } finally {
        setLoading(false);
      }
    };

    loadFacilityDetails();
  }, [facilityId, getAccessTokenSilently]);

  const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'number') {
      if (key.includes('Latitude') || key.includes('Longitude')) {
        return value.toFixed(6);
      }
      return value.toLocaleString();
    }
    
    return String(value);
  };

  const getDisplayName = (key: string): string => {
    const displayNames: { [key: string]: string } = {
      'Facility_ID': 'Facility ID',
      'Name': 'Facility Name',
      'Address': 'Address',
      'City': 'City',
      'State': 'State',
      'Zip_Code': 'ZIP Code',
      'County': 'County',
      'FIPS': 'County FIPS',
      'Latitude': 'Latitude',
      'Longitude': 'Longitude',
      'TOTAL_TANK_COUNT': 'Total Tank Count',
      'ACTIVE_TANK_COUNT': 'Active Tank Count',
      'INACTIVE_TANK_COUNT': 'Inactive Tank Count',
      'HAS_LEAK': 'Has Leak History',
      'TOTAL_LEAK_COUNT': 'Total Leak Count',
      'PROTECTION_SCORE': 'Protection Score (1-10)',
      'calculated_risk_score': 'Calculated Risk Score',
      'AVERAGE_TANK_AGE': 'Average Tank Age (years)',
      'OLDEST_TANK_YEAR': 'Oldest Tank Year',
      'NEWEST_TANK_YEAR': 'Newest Tank Year'
    };
    
    return displayNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const isImportantField = (key: string): boolean => {
    const importantFields = [
      'Name', 'Address', 'City', 'State', 'Zip_Code', 'County', 'FIPS',
      'Latitude', 'Longitude', 'TOTAL_TANK_COUNT', 'ACTIVE_TANK_COUNT',
      'HAS_LEAK', 'TOTAL_LEAK_COUNT', 'PROTECTION_SCORE', 'calculated_risk_score',
      'AVERAGE_TANK_AGE', 'OLDEST_TANK_YEAR', 'NEWEST_TANK_YEAR'
    ];
    return importantFields.includes(key);
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div>Loading facility details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#ef4444', marginTop: 0 }}>Error</h3>
          <p>{error}</p>
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!details) {
    return null;
  }

  const importantFields = Object.keys(details).filter(isImportantField);
  const otherFields = Object.keys(details).filter(key => !isImportantField(key));

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
            Facility Details: {facilityName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '4px'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{
          padding: '1.5rem',
          overflow: 'auto',
          flexGrow: 1
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginTop: 0, color: '#1f2937', borderBottom: '2px solid #3b82f6', paddingBottom: '0.5rem' }}>
              Key Information
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem',
              marginTop: '1rem'
            }}>
              {importantFields.map((key) => (
                <div key={key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb'
                }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    {getDisplayName(key)}:
                  </span>
                  <span style={{ 
                    fontWeight: key === 'HAS_LEAK' && details[key] ? '600' : 'normal',
                    color: key === 'HAS_LEAK' && details[key] ? '#ef4444' : '#1f2937'
                  }}>
                    {formatValue(key, details[key])}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {pricingInfo && (
            <PricingDisplay pricingInfo={pricingInfo} />
          )}

          {otherFields.length > 0 && (
            <div>
              <h3 style={{ color: '#1f2937', borderBottom: '2px solid #6b7280', paddingBottom: '0.5rem' }}>
                Additional Data Fields
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '0.5rem',
                marginTop: '1rem'
              }}>
                {otherFields.map((key) => (
                  <div key={key} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    <span style={{ fontWeight: '500', color: '#4b5563' }}>
                      {getDisplayName(key)}:
                    </span>
                    <span style={{ color: '#6b7280' }}>
                      {formatValue(key, details[key])}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FacilityDetails;